import { Box, Skeleton, Typography, useMediaQuery } from '@mui/material';
import DOMPurify from 'dompurify';
import { observer } from 'mobx-react-lite';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useStore } from '@stores';
import { globalTheme as theme } from '@styles';
import { LegalKeys } from '@types';
import {
  FOOTER_DIV_ID,
  logRocketHiddenHtml,
  replacePlaceholders,
} from '@utils';

export const TCPA = observer(() => {
  const {
    commonStore,
    formStore,
    partnerStore: { legalTerms, tcpaUrls },
  } = useStore();
  const { i18n, t } = useTranslation('subModules/contactInfo');
  const { t: tFooter } = useTranslation('footer');
  const language = i18n.language as 'en' | 'es';

  const { setValue, watch } = useFormContext<{
    tcpaAgreement: boolean;
    fullName: string;
  }>();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const scrollRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const [bottomInset, setBottomInset] = useState(0);

  const isPreviousModuleResultOptions =
    formStore.currentModuleName ===
    formStore.previousModule('MODULE_RESULT_OPTIONS');

  const placeholders = {
    name: logRocketHiddenHtml(watch('fullName') ?? ''),
    contactAgree: isPreviousModuleResultOptions
      ? tFooter('seeOptions')
      : tFooter('continue'),
  };

  const preCleanedHtml =
    language === 'en' ? legalTerms?.tcpaEn : legalTerms?.tcpaEs;
  const cleanedHtml = replacePlaceholders(preCleanedHtml!, placeholders);

  const isValidLegalKey = (value: string): value is LegalKeys => {
    return Object.values(LegalKeys).includes(value as LegalKeys);
  };

  const previousDoc = useRef(preCleanedHtml);
  useLayoutEffect(() => {
    if (
      previousDoc.current &&
      preCleanedHtml &&
      previousDoc.current !== preCleanedHtml
    ) {
      if (isMobile) scrollRef.current?.scrollIntoView({ block: 'start' });
      else scrollRef.current?.scrollTo({ top: 0 });
    }
    previousDoc.current = preCleanedHtml;
  }, [preCleanedHtml, isMobile]);

  useLayoutEffect(() => {
    const footer = document.getElementById(FOOTER_DIV_ID);
    if (!footer) return;
    const updateHeight = () =>
      setBottomInset(footer.getBoundingClientRect().height);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setValue('tcpaAgreement', false, { shouldValidate: true });
  }, [cleanedHtml, setValue]);

  useEffect(() => {
    const end = endRef.current;
    if (!end || !cleanedHtml) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio === 1) {
          setValue('tcpaAgreement', true, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      },
      {
        threshold: 1,
        rootMargin: `0px 0px -${isMobile ? bottomInset : 0}px 0px`,
      }
    );
    observer.observe(end);
    return () => observer.disconnect();
  }, [cleanedHtml, isMobile, bottomInset, setValue]);

  useEffect(() => {
    const container = scrollRef.current;

    if (!container) return;

    const onClick = (e: MouseEvent) => {
      const el = e.currentTarget as HTMLElement;
      const arg = el.getAttribute('data-arg');

      e.preventDefault();

      if (arg && isValidLegalKey(arg)) {
        const link = tcpaUrls[arg][language];
        if (link) {
          window.open(link, '_blank', 'noopener,noreferrer');
        }
      }
      if (arg === 'servicer') {
        commonStore.setOpenProviders(true);
      }
    };

    const links = container.querySelectorAll('[data-action="openDrawer"]');

    links.forEach((el) => {
      const linkElement = el as HTMLAnchorElement;
      linkElement.addEventListener('click', onClick);
    });

    return () => {
      links.forEach((el) => {
        const linkElement = el as HTMLAnchorElement;
        linkElement.removeEventListener('click', onClick);
      });
    };
  }, [commonStore, language, tcpaUrls, cleanedHtml]);

  const termsContent = (
    <Box
      id="tcpa"
      ref={scrollRef}
      role="region"
      aria-label={t('terms')}
      aria-busy={!cleanedHtml}
      tabIndex={0}
      data-cy="tcpaAgreement"
      sx={{
        backgroundColor: { xs: 'brandGray.lighter', md: 'transparent' },
        borderRadius: { xs: '6px', md: 0 },
        p: 2,
        flex: { md: 1 },
        minHeight: 0,
        height: 'auto',
        overflowY: { xs: 'visible', md: 'auto' },
        minWidth: 0,
        maxWidth: '100%',
        [theme.breakpoints.down('md')]: {
          '& p': { m: 0 },
          '& p + p': { mt: 1.5 },
          '& strong': { fontWeight: 600 },
          scrollbarWidth: 'auto',
          scrollbarColor: `${theme.palette.brandGray.dark} ${theme.palette.brandGray.lighter}`,
          '&::-webkit-scrollbar': { width: '11px' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'brandGray.dark',
            borderRadius: '6px',
          },
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'brandBlue.dark',
          outlineOffset: '2px',
        },
      }}
    >
      {!cleanedHtml ? (
        <Box sx={{ minHeight: '220px' }}>
          {Array.from({ length: 7 }, (_, i) => (
            <Skeleton key={i} variant="text" />
          ))}
        </Box>
      ) : (
        <>
          <Typography
            component="h2"
            sx={{
              lineHeight: { xs: 1.6, md: 1.2 },
              fontSize: { xs: 14, md: 12 },
              textAlign: { xs: 'left', md: 'center' },
              fontWeight: 700,
              color: { xs: 'text.primary', md: 'custom.tcpaText' },
              mb: 1,
            }}
          >
            {t('terms')}
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 700,
              color: { xs: 'text.primary', md: 'custom.tcpaText' },
              mb: 1,
            }}
          >
            {t('infoSharingConsent')}
          </Typography>
          <Typography
            component="div"
            sx={{
              fontSize: { xs: '14px', md: '12px' },
              lineHeight: { xs: 1.6, md: 1.5 },
              overflowWrap: { xs: 'anywhere', md: 'normal' },
              fontWeight: { xs: 400, md: 300 },
              color: { xs: 'text.primary', md: 'custom.tcpaText' },
              '& a': {
                color: { xs: 'brandBlue.darker', md: 'custom.tcpaLink' },
                ...(isMobile && {
                  fontWeight: 600,
                  textDecoration: 'underline',
                }),
                '&:hover': {
                  cursor: 'pointer',
                },
              },
              '& strong, & b': {
                fontWeight: 'bold',
              },
              '& em, & i': {
                fontStyle: 'italic',
              },
            }}
            data-tf-element-role="consent-language"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(cleanedHtml || '', {
                ADD_ATTR: [
                  'target',
                  'rel',
                  'type',
                  'start',
                  'reversed',
                  'value',
                  'data-logrocket-hidden',
                ],
              }),
            }}
          />
          <Box
            ref={endRef}
            data-testid="tcpa-end"
            sx={{ height: { xs: '1px', md: '24px' } }}
          />
        </>
      )}
    </Box>
  );

  return (
    <>
      <Typography
        sx={{
          display: { xs: 'none', md: 'block' },
          fontSize: 12,
          fontWeight: 700,
          textAlign: 'center',
          mb: 1,
          color: 'custom.inputLabel',
        }}
      >
        {t('scrollToEnableDesktop')}
      </Typography>
      {isMobile ? (
        termsContent
      ) : (
        <Box
          sx={{
            backgroundColor: 'custom.tcpaBackground',
            borderRadius: 1,
            height: '225px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {termsContent}
        </Box>
      )}
    </>
  );
});
